import type { RuleId, RulesConfig } from '@smart-link/shared';
import { ruleIdSchema } from '@smart-link/shared';
import { ChevronRight } from 'lucide-react';
import { type ChangeEvent, useState } from 'react';
import { cn } from '../lib/cn';
import { VERIFY_STRATEGY_OPTIONS, ruleLabel } from '../lib/rule-labels';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

interface RuleGroup {
  title: string;
  rules: { id: RuleId; desc: string; note?: string }[];
}

export const RULE_GROUPS: RuleGroup[] = [
  {
    title: '基础检测',
    rules: [
      { id: 'datacenterAsn', desc: 'ASN 命中数据中心/托管列表（开源 ASN 库）' },
      { id: 'proxyIp', desc: 'ASN 机构名命中 VPN/代理/匿名关键词' },
      { id: 'missingTtclid', desc: '无广告点击 ID (ttclid/fbclid/gclid) 且非广告平台来源' },
      { id: 'notInGeoAllow', desc: '国家不在地理白名单内' },
      { id: 'isDesktop', desc: '屏蔽 PC（仅放行移动端广告流量）' },
    ],
  },
  {
    title: '自动化检测',
    rules: [
      { id: 'botdBot', desc: 'BotD（FingerprintJS 开源库）判定为爬虫' },
      { id: 'webdriverFlag', desc: 'navigator.webdriver = true' },
      { id: 'headlessUa', desc: 'UA 含 HeadlessChrome/Selenium 等' },
      { id: 'hasDriverMarker', desc: 'window 上有 Selenium/CDP 全局变量' },
      { id: 'nativeFnTampered', desc: '原生函数被 anti-detect 工具改写' },
      { id: 'lowPerfJitter', desc: 'performance.now() 抖动过小' },
      { id: 'rafTimingFlat', desc: 'rAF 帧间隔过于均匀' },
    ],
  },
  {
    title: '行为分析',
    rules: [
      { id: 'noInteraction', desc: '停留 <800ms 且零交互' },
      { id: 'challengeFailed', desc: '未通过主动挑战' },
      { id: 'lowMouseEntropy', desc: '鼠标轨迹熵过低（直线移动）' },
      { id: 'syntheticTouch', desc: '合成触摸事件（force/radius 全 0）' },
      { id: 'lowScrollEntropy', desc: '滚动行为熵过低' },
      { id: 'lowClickEntropy', desc: '点击位置熵过低' },
    ],
  },
  {
    title: '移动设备验证',
    rules: [
      { id: 'mobileMismatch', desc: '移动 UA 但桌面屏幕' },
      { id: 'noMotionSensors', desc: '移动 UA 无运动传感器' },
      { id: 'desktopGpuOnMobile', desc: '移动 UA 但桌面 GPU' },
      { id: 'noTouchOnMobile', desc: '移动 UA 无触摸能力' },
      { id: 'lowDprOnMobile', desc: '移动 UA 像素密度过低' },
      { id: 'nonCoarsePointer', desc: '移动 UA 但 pointer 非 coarse' },
      { id: 'noVibrateOnMobile', desc: '非 iOS 移动 UA 无振动 API' },
      { id: 'noOrientationOnMobile', desc: '移动 UA 无屏幕方向 API' },
      {
        id: 'googlePayNotReady',
        desc: '安卓设备 Google Pay isReadyToPay 未通过',
        note: '仅 Android 设备参与；默认仅记录命中，不影响评分',
      },

      { id: 'notInAppWebview', desc: '非广告平台 in-app 浏览器（默认开）' },
    ],
  },
  {
    title: '环境一致性',
    rules: [
      { id: 'platformUaMismatch', desc: 'Platform 与 UA 不一致' },
      { id: 'missingClientHints', desc: 'Chromium 缺少 sec-ch-ua 头' },
      { id: 'clientHintsMismatch', desc: 'Client Hints 与 UA 不匹配' },
      { id: 'cssFeatureMismatch', desc: 'CSS 特性与 UA 版本不符' },
      { id: 'noStorage', desc: 'localStorage / sessionStorage 不可用' },
      { id: 'noMediaDevices', desc: '无音视频设备' },
      { id: 'zeroSpeechVoices', desc: 'speechSynthesis 返回 0 个语音' },
      { id: 'canvasNoise', desc: 'Canvas 渲染不稳定（反指纹插件）' },
      { id: 'tlsHttpMismatch', desc: '现代移动 UA 但 TLS/HTTP 版本过旧' },
    ],
  },
  {
    title: 'Probe 交叉验证',
    rules: [
      { id: 'timezoneMismatch', desc: '时区与 IP 国家不匹配' },
      { id: 'languageMismatch', desc: '语言与 IP 国家不匹配' },
      { id: 'probeVerifyIpChange', desc: 'Probe/Verify IP 不一致（代理切换）' },
    ],
  },
  {
    title: '设备识别',
    rules: [
      {
        id: 'crossLinkDevice',
        desc: '同一持久设备(UUID)访问 ≥「跨链接阈值」个不同链接',
        note: '正常广告流量只落地一个链接；跨多条多为踩点/审核/爬虫',
      },
    ],
  },
  {
    title: '严格模式规则',
    rules: [
      {
        id: 'staticDevice',
        desc: '安卓设备运动幅度近乎为 0 且无鼠标/触摸/滚动',
        note: '仅在链接开启「严格模式」时参与判定；iOS 豁免（运动权限需授权）',
      },
      {
        id: 'sameLinkReentry',
        desc: '同一持久设备(UUID)在本链接的到访次数 ≥「同链接重入阈值」',
        note: '仅在链接开启「严格模式」时参与判定',
      },
    ],
  },
  {
    title: '边缘令牌 & 观测信号',
    rules: [
      { id: 'edgeTokenMissing', desc: 'verify 未带有效 edge token（缓存 HTML / bfcache）' },
      { id: 'edgeIpDrift', desc: 'HTML→verify 间发生真实网络切换（国家或 ASN 变化）' },
      { id: 'edgeVerifyTooFast', desc: 'HTML→verify 间隔过短（自动化无需等渲染+点击）' },
      {
        id: 'notificationPermMismatch',
        desc: '通知权限状态与环境不符',
        note: '仅观测，权重 0，不改判定',
      },
      {
        id: 'noPlatformAuthenticator',
        desc: '无平台认证器（WebAuthn）',
        note: '仅观测，权重 0，不改判定',
      },
      {
        id: 'signalCollectTimeout',
        desc: '指纹收集超时降级（恶劣 in-app webview）',
        note: '仅观测，权重 0，不改判定',
      },
    ],
  },
];

// DEV 期完整性守卫:RULE_GROUPS 必须覆盖 shared 的全部 RuleId,否则新增规则会静默
// 缺失 UI 入口(既编辑不了、也无法在链接级覆盖)。admin 无测试设施,用启动期断言兜底。
if (import.meta.env.DEV) {
  const covered = new Set(RULE_GROUPS.flatMap((g) => g.rules.map((r) => r.id)));
  const missing = ruleIdSchema.options.filter((id) => !covered.has(id));
  if (missing.length) {
    console.error('[RulesEditor] RULE_GROUPS 缺少 RuleId,请补充分组:', missing);
  }
}

/** 读数字输入:空值/非法时返回 fallback(保留原值),避免 Number('')===0 触发下限校验 400。 */
function numOr(e: ChangeEvent<HTMLInputElement>, fallback: number): number {
  const v = e.currentTarget.valueAsNumber;
  return Number.isFinite(v) ? v : fallback;
}

/** 覆盖标记 + 一键恢复继承。仅在 baseline 模式(链接级覆盖)且该项已被覆盖时渲染。 */
function OverrideTag({ show, onRestore }: { show: boolean; onRestore: () => void }) {
  if (!show) return null;
  return (
    <button
      type="button"
      onClick={onRestore}
      title="点击恢复继承平台默认"
      className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-amber-100 px-1 py-px align-middle text-[10px] font-medium text-amber-700 hover:bg-amber-200 dark:bg-amber-500/15 dark:text-amber-400 dark:hover:bg-amber-500/25"
    >
      已覆盖·恢复
    </button>
  );
}

export interface RulesEditorProps {
  value: RulesConfig;
  onChange: (next: RulesConfig) => void;
  /** 提供时进入「链接级覆盖」模式:各项与 baseline 对比,不同则显示覆盖标记 + 恢复继承。 */
  baseline?: RulesConfig;
}

/**
 * 平台规则编辑器(评分阈值 + 主动挑战&地理 + 全部规则分组)。
 * 全局规则页直接编辑平台配置;链接页传入 baseline 进入覆盖模式,复用同一套 UI。
 */
export function RulesEditor({ value: draft, onChange: setDraft, baseline }: RulesEditorProps) {
  // 标量字段的覆盖标记 props：show=与 baseline 不同，onRestore=还原为 baseline 值。
  const scalarOverride = <K extends keyof RulesConfig>(key: K) => ({
    show: !!baseline && draft[key] !== baseline[key],
    onRestore: () => baseline && setDraft({ ...draft, [key]: baseline[key] }),
  });
  const geoOverridden =
    !!baseline && JSON.stringify(draft.geoAllowlist) !== JSON.stringify(baseline.geoAllowlist);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card className="gap-4 p-5">
          <h2 className="text-sm font-semibold">评分阈值</h2>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div className="space-y-1.5">
              <Label className="text-muted-foreground text-xs">
                起始分
                <OverrideTag {...scalarOverride('startScore')} />
              </Label>
              <Input
                type="number"
                value={draft.startScore}
                onChange={(e) => setDraft({ ...draft, startScore: numOr(e, draft.startScore) })}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-muted-foreground text-xs">
                通过阈值
                <OverrideTag {...scalarOverride('passThreshold')} />
              </Label>
              <Input
                type="number"
                value={draft.passThreshold}
                onChange={(e) =>
                  setDraft({ ...draft, passThreshold: numOr(e, draft.passThreshold) })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-muted-foreground text-xs">
                跨链接阈值
                <OverrideTag {...scalarOverride('crossLinkMinDistinct')} />
              </Label>
              <Input
                type="number"
                min={2}
                value={draft.crossLinkMinDistinct}
                onChange={(e) =>
                  setDraft({ ...draft, crossLinkMinDistinct: numOr(e, draft.crossLinkMinDistinct) })
                }
              />
            </div>
          </div>
        </Card>
        <Card className="gap-4 p-5">
          <h2 className="text-sm font-semibold">主动挑战 & 地理</h2>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Label htmlFor="verifyStrategy" className="font-normal">
                验证策略
                <OverrideTag {...scalarOverride('verifyStrategy')} />
              </Label>
              <select
                id="verifyStrategy"
                value={draft.verifyStrategy}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    verifyStrategy: e.target.value as RulesConfig['verifyStrategy'],
                  })
                }
                className="h-8 rounded-md border border-zinc-200 bg-white px-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              >
                {VERIFY_STRATEGY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {draft.verifyStrategy !== 'auto' && (
                <span className="text-muted-foreground flex items-center gap-1 text-xs">
                  超时
                  <Input
                    type="number"
                    value={draft.challengeTimeoutMs}
                    onChange={(e) =>
                      setDraft({ ...draft, challengeTimeoutMs: numOr(e, draft.challengeTimeoutMs) })
                    }
                    className="h-7 w-20 text-xs"
                  />
                  ms
                  <OverrideTag {...scalarOverride('challengeTimeoutMs')} />
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Checkbox
                id="redirectOnBlock"
                checked={draft.redirectOnBlock}
                onCheckedChange={(c) => setDraft({ ...draft, redirectOnBlock: c === true })}
              />
              <Label htmlFor="redirectOnBlock" className="font-normal">
                被拦截后跳转安全页
                <OverrideTag {...scalarOverride('redirectOnBlock')} />
              </Label>
              {!draft.redirectOnBlock && (
                <span className="text-muted-foreground text-xs">
                  关闭后被拦访客原地停留，拿不到任何下游 URL
                </span>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-muted-foreground text-xs">
                地理白名单（逗号分隔国家代码）
                <OverrideTag
                  show={geoOverridden}
                  onRestore={() =>
                    baseline && setDraft({ ...draft, geoAllowlist: baseline.geoAllowlist })
                  }
                />
              </Label>
              <Input
                value={draft.geoAllowlist.join(',')}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    geoAllowlist: e.target.value
                      .split(',')
                      .map((s) => s.trim().toUpperCase())
                      .filter(Boolean),
                  })
                }
                placeholder="SG,MY,TH,ID,VN"
              />
            </div>
          </div>
        </Card>
      </div>

      <div className="space-y-2">
        {RULE_GROUPS.map((group) => (
          <RuleGroupCard
            key={group.title}
            group={group}
            draft={draft}
            baseline={baseline}
            onUpdate={setDraft}
          />
        ))}
      </div>
    </div>
  );
}

function RuleGroupCard({
  group,
  draft,
  baseline,
  onUpdate,
}: {
  group: RuleGroup;
  draft: RulesConfig;
  baseline?: RulesConfig;
  onUpdate: (cfg: RulesConfig) => void;
}) {
  const [open, setOpen] = useState(true);
  // 有效值:缺省(undefined)按引擎语义 = 启用 / 权重 0,与行内渲染保持一致。
  const isEnabled = (cfg: RulesConfig, id: RuleId) => cfg.enabled[id] !== false;
  const weightOf = (cfg: RulesConfig, id: RuleId) => cfg.weights[id] ?? 0;
  const enabledCount = group.rules.filter((r) => isEnabled(draft, r.id)).length;
  // 单一真源:一条规则相对 baseline 是否被覆盖(权重或开关任一不同,按有效值比较)。
  const isOverridden = (id: RuleId) =>
    !!baseline &&
    (weightOf(draft, id) !== weightOf(baseline, id) ||
      isEnabled(draft, id) !== isEnabled(baseline, id));
  const overriddenCount = group.rules.filter((r) => isOverridden(r.id)).length;

  return (
    <Card className="gap-0 overflow-hidden p-0">
      <Button
        type="button"
        variant="ghost"
        onClick={() => setOpen((s) => !s)}
        className="h-auto w-full justify-start gap-2 rounded-none px-4 py-3 font-normal"
      >
        <ChevronRight
          className={cn('text-muted-foreground transition-transform', open && 'rotate-90')}
        />
        <span className="text-sm font-semibold">{group.title}</span>
        <Badge variant="secondary" className="ml-1">
          {enabledCount}/{group.rules.length} 启用
        </Badge>
        {overriddenCount > 0 && (
          <Badge className="ml-1 bg-amber-100 text-amber-700 hover:bg-amber-100 dark:bg-amber-500/15 dark:text-amber-400">
            {overriddenCount} 项已覆盖
          </Badge>
        )}
      </Button>
      {open && (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent [&_th]:h-8 [&_th]:text-[11px] [&_th]:uppercase [&_th]:tracking-wider">
              <TableHead className="w-16 pl-10">启用</TableHead>
              <TableHead>规则</TableHead>
              <TableHead>说明</TableHead>
              <TableHead className="text-right">扣分</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {group.rules.map(({ id, desc, note }) => {
              const enabled = draft.enabled[id] !== false;
              const overridden = isOverridden(id);
              return (
                <TableRow key={id} className={cn(!enabled && 'opacity-40')}>
                  <TableCell className="pl-10">
                    <Checkbox
                      checked={enabled}
                      onCheckedChange={(c) =>
                        onUpdate({ ...draft, enabled: { ...draft.enabled, [id]: c === true } })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      {overridden && (
                        <span
                          className="size-1.5 shrink-0 rounded-full bg-amber-500"
                          title="已覆盖平台默认"
                        />
                      )}
                      <span className="font-medium">{ruleLabel(id)}</span>
                    </div>
                    <div className="text-muted-foreground font-mono text-[10px]">{id}</div>
                    {note && (
                      <div className="mt-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
                        {note}
                      </div>
                    )}
                    {baseline && overridden && (
                      <button
                        type="button"
                        onClick={() =>
                          onUpdate({
                            ...draft,
                            weights: { ...draft.weights, [id]: baseline.weights[id] },
                            enabled: { ...draft.enabled, [id]: baseline.enabled[id] },
                          })
                        }
                        className="mt-0.5 text-[10px] text-amber-600 hover:underline dark:text-amber-400"
                      >
                        恢复继承（{isEnabled(baseline, id) ? '启用' : '停用'} · 扣{' '}
                        {weightOf(baseline, id)}）
                      </button>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">{desc}</TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number"
                      value={draft.weights[id] ?? 0}
                      onChange={(e) =>
                        onUpdate({
                          ...draft,
                          weights: { ...draft.weights, [id]: numOr(e, draft.weights[id] ?? 0) },
                        })
                      }
                      className="ml-auto h-8 w-16 text-right font-mono"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}
